import type { ComponentType } from "react";

import type {
  AdoptionPin,
  LibraryAsset,
  LibraryAssetType,
} from "@/api/library";

import { AlertMatrixConsequence } from "./AlertMatrixConsequence";
import { QuestionnaireConsequence } from "./QuestionnaireConsequence";

/**
 * The extension point type-specific pin-consequence renderers plug into (#373).
 *
 * ## What a consequence renderer is for
 *
 * `GET /v1/projects/{id}/adoptions` answers what a project pins — an `assetId`
 * and an immutable `version`, and nothing else. That is a fact about storage,
 * not something a clinician can act on: it says the project pins
 * `asset_seed_alert_matrix_default` at `v1`, and says nothing about what
 * happens differently in this project because it does. Turning a pin into that
 * sentence needs knowledge of the pinned TYPE, and there are nine asset types
 * in the contract with nine different answers.
 *
 * So the generic Summary owns everything common to every pin — reading the pin
 * set, naming the asset, saying whether the pin is behind the library head,
 * and rendering an explicit state when a type has no renderer yet — and each
 * type's own consequence is a renderer registered here.
 *
 * ## The contract a renderer signs
 *
 * 1. **It never reads adoption state of its own.** Everything it needs about
 *    the pin arrives as props, off the ONE `useProjectAdoptionsQuery` the
 *    Summary holds. This is #373 AC 4's "without duplicating adoption state",
 *    and it is enforced by construction rather than by convention: a renderer's
 *    props carry the pin, so there is nothing for a second query to add.
 *    `ui/summary/ClinicalSummary.test.tsx` asserts that no module in this
 *    directory other than the Summary itself imports that hook.
 * 2. **It renders read-only.** #373 adds no adoption mutation and no control
 *    that writes; adoption, upgrade, diff and lock controls belong to #372.
 * 3. **It may read its own type's detail.** The alert-matrix renderer below
 *    resolves the pinned version's stored content, because "what this pin does"
 *    is in that content and nowhere else. That read is server-composed and
 *    version-explicit, so nothing about the consequence is inferred in the
 *    browser — and it is keyed under the library root, which no preview-target
 *    change invalidates.
 * 4. **It states its own failures.** A renderer whose detail read fails says
 *    so; it never renders a partial or empty consequence that reads as "this
 *    pin does nothing".
 *
 * ## Adding a renderer
 *
 * Write a component taking `PinConsequenceProps`, then add one entry to
 * `PIN_CONSEQUENCE_RENDERERS`. Nothing else changes: an unregistered type
 * already renders the explicit "not shown here yet" state, so registration is
 * the whole of the wiring.
 */
export type PinConsequenceProps = {
  projectId: string;
  /** The pin, straight from the Summary's shared adoptions query. */
  pin: AdoptionPin;
  /** The catalog row naming it — the asset's type, name and current head. */
  asset: LibraryAsset;
};

export type PinConsequenceRenderer = {
  /** The one asset type this renderer speaks for. */
  assetType: LibraryAssetType;
  /**
   * What this type of pin governs, in the clinician's words. Rendered as the
   * consequence block's heading, so it names the effect rather than the asset.
   */
  heading: string;
  Component: ComponentType<PinConsequenceProps>;
};

/**
 * Every registered renderer.
 *
 * Two today. #373's scope was the seam plus one real implementation — the alert
 * matrix, the only pin type with an existing consequence surface at the time —
 * and a second entry written speculatively would have been a renderer nobody
 * had agreed the wording of. #370 adds the questionnaire once that wording
 * exists: #368 published the shared read-only vocabulary for describing a
 * stored instrument and #369 used it on the Project's adoption row, so the
 * Summary's version of the same statement is a reuse rather than a third
 * invention. The remaining seven types render the explicit unrendered state,
 * which is a true statement rather than a placeholder.
 */
export const PIN_CONSEQUENCE_RENDERERS: readonly PinConsequenceRenderer[] = [
  {
    assetType: "alert_matrix",
    heading: "What raises an alert, and where it goes",
    Component: AlertMatrixConsequence,
  },
  {
    assetType: "questionnaire",
    heading: "What is administered, and how it is scored",
    Component: QuestionnaireConsequence,
  },
];

export function consequenceRendererFor(
  assetType: LibraryAssetType,
  renderers: readonly PinConsequenceRenderer[] = PIN_CONSEQUENCE_RENDERERS,
): PinConsequenceRenderer | undefined {
  return renderers.find((renderer) => renderer.assetType === assetType);
}
